// src/pages/SchedulePosts.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Container,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Upload as UploadIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Code as CodeIcon,
  Preview as PreviewIcon,
  Publish as PublishIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import { getApiUrl } from '../config';
import PageHead from '../components/PageHead';

function SchedulePosts() {
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedKeywords, setEditedKeywords] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [success, setSuccess] = useState(null);
  const [viewTab, setViewTab] = useState('preview');

  useEffect(() => {
    fetchScheduledPosts();
  }, []);

  const fetchScheduledPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${getApiUrl()}/posts/scheduled-posts`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setScheduledPosts(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch scheduled posts');
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async (postId, date) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${getApiUrl()}/posts/${postId}/update-status`,
        {
          status: 'scheduled',
          scheduled_date: date
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Refresh posts list
      fetchScheduledPosts();
      setOpenDialog(false);
      setSelectedPost(null);
    } catch (err) {
      console.error('Error scheduling post:', err);
      setError('Failed to schedule post');
    }
  };

  const handleView = async (post) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${getApiUrl()}/posts/${post.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const fullPost = response.data.data;
      
      let formattedKeywords = fullPost.keywords;
      if (Array.isArray(fullPost.keywords)) {
        formattedKeywords = fullPost.keywords.join(', ');
      }

      setSelectedPost({
        ...fullPost,
        keywords: formattedKeywords
      });
      setEditedContent(fullPost.content || '');
      setEditedKeywords(formattedKeywords || '');
      setEditedTitle(fullPost.title || '');
      setEditedDescription(fullPost.description || '');
      setEditMode(false);
      setOpenDialog(true);
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Failed to fetch post details');
    }
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${getApiUrl()}/posts/${selectedPost.id}`,
        {
          content: editedContent,
          keywords: editedKeywords,
          title: editedTitle,
          description: editedDescription
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setSelectedPost({
        ...selectedPost,
        content: editedContent,
        keywords: editedKeywords,
        title: editedTitle,
        description: editedDescription
      });
      setEditMode(false);
      fetchScheduledPosts();
    } catch (err) {
      console.error('Error saving post:', err);
      setError('Failed to save post');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${getApiUrl()}/posts/${postId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      fetchScheduledPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete post');
    }
  };

  const handlePublishToWordPress = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${getApiUrl()}/posts/${postId}/publish-to-wordpress`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Show success message
      setSuccess('Successfully published to WordPress');
      
      // Update the post in the list
      fetchScheduledPosts();
    } catch (err) {
      console.error('Error publishing to WordPress:', err);
      const errorMessage = err.response?.data?.error || 'Failed to publish to WordPress';
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <CircularProgress size={60} thickness={4} />
        </motion.div>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <PageHead 
        title="Scheduled Posts" 
        description="Manage and schedule your AutoBlogPilot content"
      />
      <Box
        sx={{
          background: 'linear-gradient(145deg, #f6f8ff 0%, #f0f4ff 100%)',
          borderRadius: 4,
          p: 4,
          minHeight: '90vh',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box mb={4}>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Scheduled Posts
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Manage and review your scheduled content
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          {scheduledPosts.length > 0 ? (
            <Grid container spacing={3}>
              {scheduledPosts.map((post, index) => (
                <Grid item xs={12} md={6} key={post.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        },
                      }}
                    >
                      <Box p={3}>
                        <Box display="flex" alignItems="center" mb={2}>
                          <Box
                            sx={{
                              background: 'rgba(33, 150, 243, 0.1)',
                              borderRadius: '50%',
                              p: 1,
                              mr: 2,
                              color: 'primary.main',
                            }}
                          >
                            <ScheduleIcon />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="h6" fontWeight="medium" gutterBottom>
                              {post.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Scheduled for {new Date(post.scheduled_date).toLocaleDateString()} at {new Date(post.scheduled_date).toLocaleTimeString()}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box 
                          sx={{ 
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'flex-end'
                          }}
                        >
                          <Tooltip title="View Post">
                            <IconButton
                              onClick={() => handleView(post)}
                              sx={{ 
                                color: 'primary.main',
                                '&:hover': { background: 'rgba(33, 150, 243, 0.1)' }
                              }}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Post">
                            <IconButton
                              onClick={() => handleDelete(post.id)}
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { background: 'rgba(211, 47, 47, 0.1)' }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Publish to WordPress">
                            <IconButton
                              onClick={() => handlePublishToWordPress(post.id)}
                              sx={{ 
                                color: '#21759b',
                                '&:hover': { background: 'rgba(33, 117, 155, 0.1)' }
                              }}
                            >
                              <UploadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.8)',
                textAlign: 'center',
              }}
            >
              <Typography color="text.secondary">
                No posts are currently scheduled.
              </Typography>
            </Paper>
          )}
        </motion.div>
      </Box>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component="h2" fontWeight="bold">
              {editMode ? 'Edit Post' : selectedPost?.title}
            </Typography>
            <IconButton onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {editMode ? (
            // Edit Mode Content
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Title"
                fullWidth
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                sx={{ mb: 3 }}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                sx={{ mb: 3 }}
              />
              <TextField
                label="Content"
                fullWidth
                multiline
                rows={8}
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                sx={{ mb: 3 }}
              />
              <TextField
                label="Keywords"
                fullWidth
                value={editedKeywords}
                onChange={(e) => setEditedKeywords(e.target.value)}
                helperText="Separate keywords with commas"
              />
            </Box>
          ) : (
            // View Mode Content
            <Box sx={{ mt: 2 }}>
              <Tabs 
                value={viewTab} 
                onChange={(e, newValue) => setViewTab(newValue)}
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab 
                  icon={<PreviewIcon />} 
                  label="Preview" 
                  value="preview"
                  sx={{ textTransform: 'none' }}
                />
                <Tab 
                  icon={<CodeIcon />} 
                  label="HTML" 
                  value="html"
                  sx={{ textTransform: 'none' }}
                />
              </Tabs>

              {viewTab === 'preview' ? (
                <Box>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography paragraph sx={{ mb: 3 }}>
                    {selectedPost?.description || 'No description available'}
                  </Typography>

                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Content
                  </Typography>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3,
                      mb: 3,
                      borderRadius: 2,
                      background: 'rgba(0, 0, 0, 0.02)',
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        mt: 2,
                        mb: 1,
                        fontWeight: 'bold',
                      },
                      '& p': {
                        mb: 2,
                        lineHeight: 1.7,
                      },
                      '& ul, & ol': {
                        mt: 1,
                        mb: 2,
                        pl: 3,
                      },
                      '& li': {
                        mb: 1,
                      },
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: 1,
                        my: 2,
                      },
                      '& blockquote': {
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        pl: 2,
                        py: 1,
                        my: 2,
                        fontStyle: 'italic',
                        bgcolor: 'rgba(0, 0, 0, 0.03)',
                      },
                      '& code': {
                        fontFamily: 'monospace',
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                        p: 0.5,
                        borderRadius: 0.5,
                      },
                      '& pre': {
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                        p: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                      },
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: selectedPost?.content || '' }} />
                  </Paper>

                  <Box 
                    sx={{ 
                      display: 'flex',
                      gap: 2,
                      flexWrap: 'wrap',
                      mb: 3,
                    }}
                  >
                    {selectedPost?.keywords && 
                      (Array.isArray(selectedPost.keywords) ? 
                        selectedPost.keywords : 
                        selectedPost.keywords.split(',')
                      ).map((keyword, index) => (
                        <Chip
                          key={index}
                          label={keyword.trim()}
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(33, 150, 243, 0.1)',
                            color: 'primary.main',
                          }}
                        />
                      ))
                    }
                  </Box>

                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Schedule
                  </Typography>
                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon fontSize="small" color="primary" />
                    {selectedPost?.scheduled_date ? 
                      new Date(selectedPost.scheduled_date).toLocaleString() : 
                      'Not scheduled'
                    }
                  </Typography>
                </Box>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'rgba(0, 0, 0, 0.02)',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                  }}
                >
                  {selectedPost?.content || ''}
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          {editMode ? (
            <>
              <Button
                onClick={() => setEditMode(false)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { background: 'rgba(0,0,0,0.05)' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                startIcon={<SaveIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                }}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <Button
              onClick={handleEdit}
              variant="contained"
              startIcon={<EditIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
              }}
            >
              Edit Post
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SchedulePosts;
